import { Analytics } from '@util/Analytics';
import React, { ReactElement } from 'react';
import { ScreenType, SelectScreenConfig } from '../../../config/FlowTypes';
import { ScreenWrapper } from '../../styles/Containers';
import { NavigationProps } from '../FlowNavigation';
import { SelectLayoutConfig, SingleSelectLayout } from '../layouts/SingleSelectLayout';
import { SELECT_DELAY_DEFAULT_MS } from '../SingleSelectDefaultDelay';
import { onSingleSelectSubmit } from '../SingleSelectScreen';

export type MedicareAccessSafetyCheckScreenConfig<ScreenNames extends string> = SelectScreenConfig<
  ScreenType.MedicareAccessSafetyCheck,
  SelectLayoutConfig,
  ScreenNames
> & { delayInMsBetweenClickAndNavigate?: number };

type Props<ScreenNames extends string> = NavigationProps<
  MedicareAccessSafetyCheckScreenConfig<ScreenNames>,
  ScreenNames
>;

/**
 * A Yes/No safety-check confirmation (suicide or harm-others ideation). Behaves like a
 * SingleSelect screen, but tracks the PHQ-9 escalation the moment "Yes" is selected — escalated
 * users land on the crisis screen next and may drop off there, so tracking any later would
 * undercount escalations.
 */
export const MedicareAccessSafetyCheckScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const delayInMsBetweenClickAndNavigate =
    props.delayInMsBetweenClickAndNavigate ?? SELECT_DELAY_DEFAULT_MS;

  function onSelected(selected: string) {
    if (selected === 'Yes') {
      Analytics.track({ event: Analytics.Event.o_phq9_escalation });
    }
    // Small action timeout so user can actually see what option was clicked
    setTimeout(() => onSingleSelectSubmit({ props, selected }), delayInMsBetweenClickAndNavigate);
  }

  return (
    <ScreenWrapper>
      <SingleSelectLayout
        onSelected={onSelected}
        {...props.layout}
        onShowToast={props.onShowToast}
      />
    </ScreenWrapper>
  );
};
